import React, { useState } from 'react';
import useInvitesHook from '../../../api/panel/userInvites';

export default function AdminInvitePanel() {
    // form state
    const [email, setEmail] = useState('');
    const [hours, setHours] = useState(48);
    const [result, setResult] = useState(null);

    // list state
    const [page, setPage] = useState(1);
    const limit = 25;

    const {
        listInvites,
        createInvite,
        deleteInvite,
        approveInvite,
        rejectInvite,
    } = useInvitesHook({ page, limit });

    const { data, isLoading, isError, error, isFetching } = listInvites;
    const { mutateAsync: createAsync, isLoading: isCreating } = createInvite;
    const { mutateAsync: deleteAsync, isLoading: isDeleting } = deleteInvite;
    const { mutateAsync: approveAsync, isLoading: isApproving } = approveInvite;
    const { mutateAsync: rejectAsync, isLoading: isRejecting } = rejectInvite;

    const submit = async (e) => {
        e.preventDefault();
        const payload = {
            email: email.trim() ? email.trim() : undefined,
            hours: Number(hours) || 48,
        };
        const res = await createAsync(payload);
        setResult(res);
    };

    const copyNewResult = async () => {
        if (!result?.link) return;
        try {
            await navigator.clipboard.writeText(result.link);
        } catch (err) {
            console.error('Failed to copy invite link:', err);
        }
    };

    const copyLink = async (link) => {
        try {
            await navigator.clipboard.writeText(link);
        } catch (err) {
            console.error('Failed to copy invite link:', err);
        }
    };

    const onDelete = async (id) => {
        const ok = window.confirm('Delete this invite?');
        if (!ok) return;
        await deleteAsync(id);
    };

    const onApprove = async (id) => {
        await approveAsync(id);
    };

    const onReject = async (id) => {
        await rejectAsync(id);
    };

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                <h5 className="card-title mb-3">Invite (Google or local via approval)</h5>

                {/* Create form */}
                <form onSubmit={submit} className="row g-2">
                    <div className="col-md-6">
                        <label className="form-label">Email (optional)</label>
                        <input
                            type="email"
                            className="form-control"
                            placeholder="someone@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <div className="form-text">
                            Leave empty to allow any Google account to use this invite.
                        </div>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label">Expires in (hours)</label>
                        <input
                            type="number"
                            min="1"
                            className="form-control"
                            value={hours}
                            onChange={(e) => setHours(e.target.value)}
                        />
                    </div>

                    <div className="col-md-3 d-flex invites-align align-items-end">
                        <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={isCreating}
                        >
                            {isCreating ? (
                                <span className="spinner-border spinner-border-sm" />
                            ) : (
                                'Create invite'
                            )}
                        </button>
                    </div>
                </form>

                {result && (
                    <div className="alert alert-success mt-3">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <div className="me-auto">
                                <div className="fw-bold mb-1">Invite link</div>
                                <div className="small text-break">{result.link}</div>
                                <div className="small text-muted mt-1">
                                    Expires: {new Date(result.expiresAt).toLocaleString()}
                                </div>
                            </div>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={copyNewResult}
                            >
                                Copy
                            </button>
                            <a
                                className="btn btn-outline-primary btn-sm"
                                href={result.link}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Open
                            </a>
                        </div>
                    </div>
                )}

                {/* List */}
                <hr className="my-4" />
                <div className="d-flex align-items-center mb-2">
                    <h6 className="mb-0">Existing invites</h6>
                    <button
                        className="btn btn-outline-secondary btn-sm ms-auto"
                        onClick={() => listInvites.refetch()}
                        disabled={isFetching}
                    >
                        {isFetching ? (
                            <span className="spinner-border spinner-border-sm" />
                        ) : (
                            'Refresh'
                        )}
                    </button>
                </div>

                {isLoading ? (
                    <div className="text-muted">Loading…</div>
                ) : isError ? (
                    <div className="alert alert-danger">
                        {error?.message || 'Failed to load invites'}
                    </div>
                ) : data?.data?.length ? (
                    <>
                        <div className="table-responsive">
                            <table className="table table-sm align-middle">
                                <thead>
                                <tr>
                                    <th>Created</th>
                                    <th>Email</th>
                                    <th>Token</th>
                                    <th>Expires</th>
                                    <th>Status</th>
                                    <th>Link</th>
                                    <th style={{ width: 180 }}>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {data.data.map((inv) => {
                                    const expired =
                                        inv.expiresAt && new Date(inv.expiresAt) < new Date();
                                    const statusBadge = (() => {
                                        if (inv.status === 'submitted')
                                            return <span className="badge bg-warning text-dark">Submitted</span>;
                                        if (inv.consumedAt || inv.status === 'consumed')
                                            return (
                                                <span className="badge bg-success">
                            Used {new Date(inv.consumedAt).toLocaleString()}
                          </span>
                                            );
                                        if (expired || inv.status === 'expired')
                                            return <span className="badge bg-secondary">Expired</span>;
                                        if (inv.status === 'rejected')
                                            return <span className="badge bg-danger">Rejected</span>;
                                        return <span className="badge bg-info">Not used</span>;
                                    })();

                                    return (
                                        <tr key={inv._id} className={expired ? 'table-warning' : ''}>
                                            <td>{new Date(inv.createdAt).toLocaleString()}</td>
                                            <td>{inv.email || <span className="text-muted">any</span>}</td>
                                            <td className="text-truncate" style={{ maxWidth: 180 }}>
                                                {inv.token}
                                            </td>
                                            <td>{new Date(inv.expiresAt).toLocaleString()}</td>
                                            <td>{statusBadge}</td>
                                            <td className="text-truncate" style={{ maxWidth: 220 }}>
                                                <a href={inv.link} target="_blank" rel="noreferrer">
                                                    {inv.link}
                                                </a>
                                            </td>
                                            <td>
                                                {inv.status === 'submitted' ? (
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            className="btn btn-success"
                                                            disabled={isApproving}
                                                            onClick={() => onApprove(inv._id)}
                                                        >
                                                            {isApproving ? (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            ) : (
                                                                'Approve'
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-danger"
                                                            disabled={isRejecting}
                                                            onClick={() => onReject(inv._id)}
                                                        >
                                                            {isRejecting ? (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            ) : (
                                                                'Reject'
                                                            )}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            className="btn btn-outline-secondary"
                                                            onClick={() => copyLink(inv.link)}
                                                        >
                                                            Copy
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-danger"
                                                            disabled={isDeleting}
                                                            onClick={() => onDelete(inv._id)}
                                                        >
                                                            {isDeleting ? (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            ) : (
                                                                'Delete'
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        {/* Simple pager */}
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                Page {data.page} / {data.pages} • Total {data.total}
                            </small>
                            <div className="btn-group">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    Prev
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={data.page >= data.pages}
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-muted">No invites found.</div>
                )}
            </div>
        </div>
    );
}


